import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageCircle, X, Bot } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import Chatbot from "./Chatbot";

const WhatsAppButton = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const phoneNumber = "6281111110744"; // WhatsApp number format: country code + number without +
  const cardRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(t.whatsapp.defaultMessage);
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
    setIsOpen(false);
  };

  const handleChatbotClick = () => {
    setShowChatbot(true);
    setIsOpen(false);
  };

  const handleBackToMenu = () => {
    setShowChatbot(false);
    setIsOpen(true);
  };

  return (
    <>
      {/* Chatbot */}
      {showChatbot && (
        <Chatbot 
          onClose={() => {
            setShowChatbot(false);
            setIsOpen(false);
          }} 
          onBack={handleBackToMenu}
        />
      )}
      
      {/* Floating WhatsApp Button */}
      <div
        className="fixed z-40"
        style={{
          right: "max(1rem, env(safe-area-inset-right))",
          // Clears the "Built with Spline" watermark that the background iframe pins to
          // the bottom-right of the viewport, and the iOS home indicator.
          bottom: "calc(max(1rem, env(safe-area-inset-bottom)) + 3rem)",
        }}
      >
        {!isOpen ? (
          <Button
            size="lg"
            className="rounded-full h-14 w-14 p-0 shadow-lg hover:shadow-xl transition-all"
            onClick={() => setIsOpen(true)}
            aria-label="Open chat options"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
        ) : (
          <Card ref={cardRef} className="shadow-xl w-80 animate-fade-up">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-lg">{t.whatsapp.title}</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t.whatsapp.subtitle}
              </p>
              <Button
                className="w-full"
                variant="outline"
                onClick={handleChatbotClick}
              >
                <Bot className="h-4 w-4 mr-2" />
                {t.whatsapp.chatbot}
              </Button>
              <Button
                className="w-full"
                onClick={handleWhatsAppClick}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                {t.whatsapp.whatsapp}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default WhatsAppButton;
